import React, { useEffect, useState, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ShoppingBag, Leaf, Sun, MapPin, BellRing, Star, Trash2 } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useCart } from "../contexts/CartContext";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import Seo from "../components/Seo";
import { StarRating } from "../components/StarRating";
import { track } from "../lib/track";

export default function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [notifyEmail, setNotifyEmail] = useState("");
  const [notifying, setNotifying] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [reviewForm, setReviewForm] = useState({ name: "", rating: 0, comment: "", website: "" });
  const [submittingReview, setSubmittingReview] = useState(false);
  const { addItem } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  const loadReviews = (pid) => api.get(`/products/${pid}/reviews`).then(({ data }) => setReviews(data)).catch(() => {});
  const trackedRef = useRef(null);

  useEffect(() => {
    api.get(`/products/${id}`).then(({ data }) => {
      setProduct(data);
      loadReviews(data.product_id);
      if (trackedRef.current !== data.product_id) {
        trackedRef.current = data.product_id;
        track("product_view", { product_id: data.product_id, name: data.name });
      }
    }).catch(() => setProduct(false));
  }, [id]);

  const submitReview = async (e) => {
    e.preventDefault();
    if (!reviewForm.name.trim()) { toast.error("Please add your name."); return; }
    if (!reviewForm.rating) { toast.error("Please pick a star rating."); return; }
    setSubmittingReview(true);
    try {
      await api.post(`/products/${product.product_id}/reviews`, reviewForm);
      toast.success("Thanks for your review!");
      setReviewForm({ name: "", rating: 0, comment: "", website: "" });
      await loadReviews(product.product_id);
      const { data } = await api.get(`/products/${product.product_id}`);
      setProduct(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmittingReview(false);
    }
  };

  const deleteReview = async (review_id) => {
    try {
      await api.delete(`/admin/reviews/${review_id}`);
      setReviews((rs) => rs.filter((r) => r.review_id !== review_id));
      toast.success("Review removed");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const submitNotify = async (e) => {
    e.preventDefault();
    if (!notifyEmail) return;
    setNotifying(true);
    try {
      const { data } = await api.post("/stock-notifications", { product_id: product.product_id, email: notifyEmail });
      toast.success(data.message);
      setNotifyEmail("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setNotifying(false);
    }
  };

  if (product === null) return <div className="max-w-7xl mx-auto px-6 py-20 text-earth font-display text-3xl">Loading…</div>;
  if (product === false) return <div className="max-w-7xl mx-auto px-6 py-20 text-earth">Not found.</div>;

  const outOfStock = product.stock !== undefined && product.stock <= 0;

  const seoDescription = (product.description ||
    `${product.name}. Naturally sourced, non-GMO seeds hand-packed in Amador County, CA.`).slice(0, 158);
  const productJsonLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.name,
    image: product.image_url ? [product.image_url] : undefined,
    description: seoDescription,
    category: product.category,
    brand: { "@type": "Brand", name: "Rooted in Amador" },
    offers: {
      "@type": "Offer",
      price: product.price.toFixed(2),
      priceCurrency: "USD",
      availability: product.stock > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
      url: typeof window !== "undefined" ? window.location.href : "",
    },
  };
  if (product.rating_count > 0) {
    productJsonLd.aggregateRating = {
      "@type": "AggregateRating",
      ratingValue: (product.rating_avg || 0).toFixed(1),
      reviewCount: product.rating_count,
      bestRating: "5",
      worstRating: "1",
    };
  }
  if (reviews.length > 0) {
    productJsonLd.review = reviews.slice(0, 10).map((r) => ({
      "@type": "Review",
      author: { "@type": "Person", name: r.name },
      datePublished: r.created_at,
      reviewRating: { "@type": "Rating", ratingValue: r.rating, bestRating: "5", worstRating: "1" },
      ...(r.comment ? { reviewBody: r.comment } : {}),
    }));
  }

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12" data-testid="product-detail-page">
      <Seo
        title={product.name}
        description={seoDescription}
        path={`/product/${product.product_id}`}
        image={product.image_url}
        type="product"
        jsonLd={productJsonLd}
      />
      <Link to="/shop" className="inline-flex items-center gap-2 text-xs tracking-wider-2 uppercase text-earth hover:text-darkRoast mb-10">
        <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Back to shop
      </Link>

      <div className="grid lg:grid-cols-2 gap-16">
        <div className="relative aspect-square overflow-hidden bg-surface border border-darkRoast/10 rounded-sm">
          <img
            src={product.image_url}
            alt={product.name}
            className={`w-full h-full object-cover ${outOfStock ? "opacity-50" : ""}`}
          />
          {outOfStock && (
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="bg-darkRoast/80 text-cream text-xs tracking-wider-2 uppercase px-4 py-2 rounded-full">
                Out of Stock
              </span>
            </div>
          )}
        </div>

        <div className="flex flex-col">
          <div className="text-xs tracking-wider-2 uppercase text-sage mb-3">{product.category}</div>
          <h1 className="font-display text-4xl sm:text-5xl text-darkRoast leading-tight" data-testid="product-name">{product.name}</h1>
          <div className="flex items-baseline gap-4 mt-6">
            <span className="font-display text-4xl text-darkRoast">${product.price.toFixed(2)}</span>
            <span className="text-sm text-earth">/ {product.packet_size_g}g packet</span>
          </div>
          {product.rating_count > 0 && (
            <a href="#reviews" className="mt-3 inline-block" data-testid="product-rating-summary">
              <StarRating value={product.rating_avg || 0} count={product.rating_count} />
            </a>
          )}

          <p className="mt-8 text-earth leading-relaxed max-w-lg">{product.description}</p>

          <div className="mt-8 flex flex-wrap gap-x-8 gap-y-3 text-xs tracking-wider-2 uppercase text-earth">
            <span className="flex items-center gap-2"><Leaf className="w-4 h-4 text-sage" strokeWidth={1.5} /> Non-GMO</span>
            <span className="flex items-center gap-2"><Sun className="w-4 h-4 text-sage" strokeWidth={1.5} /> Naturally Sourced</span>
            <span className="flex items-center gap-2"><MapPin className="w-4 h-4 text-sage" strokeWidth={1.5} /> Packed in Amador</span>
          </div>

          <div className="mt-10">
            {outOfStock ? (
              <div className="space-y-4 max-w-sm" data-testid="out-of-stock-section">
                <div className="inline-block bg-darkRoast/5 border border-darkRoast/10 text-earth text-xs tracking-wider-2 uppercase px-4 py-2 rounded-full">
                  Out of Stock
                </div>
                <p className="text-sm text-earth">
                  This variety is currently sold out. Drop your email and we'll let you know the moment it's back.
                </p>
                <form onSubmit={submitNotify} className="flex gap-2" data-testid="notify-form">
                  <input
                    type="email"
                    required
                    value={notifyEmail}
                    onChange={(e) => setNotifyEmail(e.target.value)}
                    placeholder="you@email.com"
                    className="flex-1 bg-transparent border-b border-darkRoast/20 py-2 text-sm focus:outline-none focus:border-sage placeholder:text-earth/50"
                    data-testid="notify-email-input"
                  />
                  <button
                    type="submit"
                    disabled={notifying}
                    className="inline-flex items-center gap-2 bg-darkRoast text-cream rounded-full px-5 py-2.5 text-xs tracking-wider-2 uppercase hover:bg-sage transition-colors disabled:opacity-60"
                    data-testid="notify-submit"
                  >
                    <BellRing className="w-3.5 h-3.5" strokeWidth={1.5} /> {notifying ? "Adding…" : "Notify me"}
                  </button>
                </form>
              </div>
            ) : (
              <div className="flex items-center gap-4" data-testid="add-to-cart-section">
                <div className="flex items-center border border-darkRoast/20 rounded-full">
                  <button onClick={() => setQty(Math.max(1, qty - 1))} className="px-4 py-3 text-darkRoast hover:text-sage" data-testid="qty-decrease">−</button>
                  <span className="px-4 text-darkRoast" data-testid="qty-value">{qty}</span>
                  <button onClick={() => setQty(Math.min(qty + 1, product.stock))} className="px-4 py-3 text-darkRoast hover:text-sage" data-testid="qty-increase">+</button>
                </div>
                <button
                  onClick={() => { const r = addItem(product, qty); if (r.added && !r.capped) { toast.success("Added to cart"); track("add_to_cart", { product_id: product.product_id, name: product.name, qty }); } }}
                  className="flex-1 inline-flex items-center justify-center gap-2 bg-darkRoast text-cream rounded-full px-6 py-3.5 text-sm tracking-wider-2 uppercase hover:bg-sage transition-colors"
                  data-testid="add-to-cart-btn"
                >
                  <ShoppingBag className="w-4 h-4" strokeWidth={1.5} /> Add to cart
                </button>
                <button
                  onClick={() => { addItem(product, qty); navigate("/cart"); }}
                  className="hidden sm:inline-flex items-center justify-center px-6 py-3.5 text-sm tracking-wider-2 uppercase border border-darkRoast/20 rounded-full hover:border-sage hover:text-sage"
                  data-testid="buy-now-btn"
                >
                  Buy now
                </button>
              </div>
            )}
          </div>

          <div className="mt-12 pt-8 border-t border-darkRoast/10 text-sm text-earth">
            {!outOfStock && (
              <p>
                <strong className="text-darkRoast font-medium">Stock:</strong> {product.stock} packets available · ships from Amador County, CA.
              </p>
            )}
            {product.slug && (
              <Link
                to={`/grow/${product.slug}`}
                className="inline-flex items-center gap-2 mt-4 text-darkRoast hover:text-sage text-xs tracking-wider-2 uppercase"
                data-testid="read-grow-guide-link"
              >
                Read the growing guide →
              </Link>
            )}
          </div>
        </div>
      </div>

      <section id="reviews" className="mt-20 pt-12 border-t border-darkRoast/10 max-w-3xl" data-testid="reviews-section">
        <div className="flex items-center gap-4 mb-8">
          <h2 className="font-display text-3xl text-darkRoast">Reviews</h2>
          {product.rating_count > 0 && <StarRating value={product.rating_avg || 0} count={product.rating_count} />}
        </div>

        <div className="space-y-6 mb-12">
          {reviews.length === 0 && <p className="text-earth text-sm" data-testid="no-reviews">No reviews yet. Be the first to share how this seed grew for you.</p>}
          {reviews.map((r) => (
            <div key={r.review_id} className="border-b border-darkRoast/5 pb-5" data-testid={`review-${r.review_id}`}>
              <div className="flex items-center justify-between gap-3">
                <StarRating value={r.rating} showCount={false} size="w-3.5 h-3.5" />
                {user?.role === "admin" && (
                  <button onClick={() => deleteReview(r.review_id)} className="text-earth hover:text-terracotta" data-testid={`review-delete-${r.review_id}`}>
                    <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                  </button>
                )}
              </div>
              <div className="text-sm font-medium text-darkRoast mt-2">{r.name}</div>
              <div className="text-xs text-earth">{new Date(r.created_at).toLocaleDateString()}</div>
              {r.comment && <p className="text-sm text-earth mt-2 leading-relaxed">{r.comment}</p>}
            </div>
          ))}
        </div>

        <form onSubmit={submitReview} className="bg-surface p-6 rounded-sm space-y-4" data-testid="review-form">
          <h3 className="font-display text-2xl text-darkRoast">Write a review</h3>
          <div>
            <div className="text-xs tracking-wider-2 uppercase text-earth mb-2">Your rating</div>
            <div className="flex gap-1" data-testid="review-rating-input">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  type="button"
                  key={n}
                  onClick={() => setReviewForm((f) => ({ ...f, rating: n }))}
                  data-testid={`review-star-${n}`}
                  aria-label={`${n} star${n > 1 ? "s" : ""}`}
                >
                  <Star className={`w-7 h-7 ${n <= reviewForm.rating ? "text-sage fill-sage" : "text-darkRoast/20"}`} strokeWidth={1.5} />
                </button>
              ))}
            </div>
          </div>
          <input
            value={reviewForm.name}
            onChange={(e) => setReviewForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="Your name"
            className="w-full bg-transparent border-b border-darkRoast/20 py-2 text-sm focus:outline-none focus:border-sage placeholder:text-earth/50"
            data-testid="review-name-input"
          />
          <textarea
            value={reviewForm.comment}
            onChange={(e) => setReviewForm((f) => ({ ...f, comment: e.target.value }))}
            placeholder="How did this seed grow for you? (optional)"
            rows={4}
            className="w-full bg-transparent border-b border-darkRoast/20 py-2 text-sm focus:outline-none focus:border-sage placeholder:text-earth/50 resize-none"
            data-testid="review-comment-input"
          />
          <input
            type="text"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            value={reviewForm.website}
            onChange={(e) => setReviewForm((f) => ({ ...f, website: e.target.value }))}
            style={{ position: "absolute", left: "-9999px", width: "1px", height: "1px", opacity: 0 }}
          />
          <button
            type="submit"
            disabled={submittingReview}
            className="bg-darkRoast text-cream rounded-full px-6 py-3 text-xs tracking-wider-2 uppercase hover:bg-sage transition-colors disabled:opacity-60"
            data-testid="review-submit"
          >
            {submittingReview ? "Submitting…" : "Submit review"}
          </button>
        </form>
      </section>
    </div>
  );
}
